<template>
  <main class="content">
    <!-- 登录视图 -->
    <form v-if="!token" class="login-box station-form" @submit.prevent="doLogin">
      <div class="form-title">管理员登录</div>
      <label class="field">
        <span>用户名</span>
        <input v-model.trim="loginForm.username" autocomplete="username" required />
      </label>
      <label class="field">
        <span>密码</span>
        <input v-model="loginForm.password" type="password" autocomplete="current-password" required />
      </label>
      <div v-if="loginError" class="login-error">{{ loginError }}</div>
      <button type="submit" class="btn primary" :disabled="logging">
        {{ logging ? '登录中…' : '登录' }}
      </button>
    </form>

    <!-- 管理视图 -->
    <template v-else>
      <div class="toolbar">
        <template v-if="view === 'list'">
          <input v-model.trim="keyword" class="search" placeholder="搜索站点名称 / 地址" />
          <button class="btn-add" @click="view = 'add'">+ 添加充电站</button>
          <button class="btn-logout" @click="logout">退出登录</button>
        </template>
      </div>

      <div v-if="loading" class="tip">加载中…</div>
      <div v-else-if="loadError" class="tip error">
        {{ loadError }}
        <button class="retry" @click="refresh">重试</button>
      </div>

      <StationList
        v-else-if="view === 'list'"
        :stations="filteredStations"
        :selected-id="selectedId"
        editable
        @select="selectedId = $event.id"
        @edit="openEdit"
        @remove="onRemove"
      />

      <StationForm
        v-else-if="view === 'add'"
        @save="onSave"
        @cancel="view = 'list'"
      />

      <StationForm
        v-else-if="view === 'edit'"
        :key="editing.id"
        :initial="editing"
        @save="onSave"
        @cancel="view = 'list'"
      />
    </template>
  </main>
</template>

<script>
import StationList from '../components/StationList.vue'
import StationForm from '../components/StationForm.vue'
import { listStations, addStation, updateStation, removeStation, login } from '../data/stations'

export default {
  name: 'AdminView',
  components: { StationList, StationForm },
  data() {
    return {
      token: localStorage.getItem('cs_admin_token') || '',
      loginForm: { username: '', password: '' },
      loginError: '',
      logging: false,
      stations: [],
      keyword: '',
      selectedId: '',
      view: 'list', // list | add | edit
      editing: null,
      loading: true,
      loadError: ''
    }
  },
  computed: {
    filteredStations() {
      const kw = this.keyword.toLowerCase()
      if (!kw) return this.stations
      return this.stations.filter(
        s => s.name.toLowerCase().includes(kw) || s.address.toLowerCase().includes(kw)
      )
    }
  },
  created() {
    if (this.token) this.refresh()
  },
  // 每次离开管理员页面时，自动清除登录凭据，下次进入需重新登录
  beforeRouteLeave(to, from, next) {
    this.logout()
    next()
  },
  methods: {
    async doLogin() {
      this.logging = true
      this.loginError = ''
      try {
        const { token } = await login(this.loginForm)
        this.token = token
        localStorage.setItem('cs_admin_token', token)
        this.refresh()
      } catch (e) {
        this.loginError = e.message
      } finally {
        this.logging = false
      }
    },
    logout() {
      localStorage.removeItem('cs_admin_token')
      this.token = ''
      this.view = 'list'
      this.editing = null
    },
    handleUnauthorized() {
      this.logout()
      this.view = 'list'
    },
    async refresh() {
      this.loading = true
      this.loadError = ''
      try {
        this.stations = await listStations()
      } catch (e) {
        this.loadError = e.message
      } finally {
        this.loading = false
      }
    },
    openEdit(st) {
      this.editing = st
      this.view = 'edit'
    },
    async onSave(form) {
      try {
        let saved
        if (this.view === 'edit') {
          saved = await updateStation(this.editing.id, form)
          const i = this.stations.findIndex(s => s.id === saved.id)
          if (i !== -1) this.stations.splice(i, 1, saved)
        } else {
          saved = await addStation(form)
          this.stations.push(saved)
        }
        this.selectedId = saved.id
        this.view = 'list'
      } catch (e) {
        if (e.status === 401) {
          this.handleUnauthorized()
          window.alert('登录已过期，请重新登录')
        } else {
          window.alert(e.message)
        }
      }
    },
    async onRemove(st) {
      if (!window.confirm(`确定删除「${st.name}」吗？`)) return
      try {
        await removeStation(st.id)
        this.stations = this.stations.filter(s => s.id !== st.id)
        if (this.selectedId === st.id) this.selectedId = ''
      } catch (e) {
        if (e.status === 401) {
          this.handleUnauthorized()
          window.alert('登录已过期，请重新登录')
        } else {
          window.alert(e.message)
        }
      }
    }
  }
}
</script>

<style scoped>
.login-box {
  max-width: 360px;
  margin: 40px auto 0;
  padding: 20px;
}
.form-title {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 12px;
}
.field {
  display: block;
  margin-bottom: 10px;
}
.field span {
  display: block;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
}
.field input {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 13px;
  outline: none;
}
.field input:focus {
  border-color: #2563eb;
}
.login-error {
  font-size: 12px;
  color: #dc2626;
  margin-bottom: 8px;
}
.btn {
  width: 100%;
  padding: 9px;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
}
.btn.primary {
  background: #2563eb;
  color: #fff;
}
.btn.primary:disabled {
  opacity: 0.6;
  cursor: default;
}
.btn-logout {
  padding: 8px 14px;
  background: #fff;
  color: #475569;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
}
.btn-logout:hover {
  border-color: #dc2626;
  color: #dc2626;
}
</style>
